import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import { submitContact } from '../lib/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
    preferredContact: 'Email',
    consent: false,
    website: '', // honeypot
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      toast.error('Please accept the privacy policy to continue.');
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(formData);
      if (res.success) {
        toast.success(`Message sent! Your reference is ${res.reference}. Our team will contact you shortly.`);
        setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '', preferredContact: 'Email', consent: false, website: '' });
      } else {
        toast.error('Something went wrong. Please call our 24/7 line.');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Please try again or call us directly.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: Phone, title: 'Phone', value: '+44 (0) 1753 720055', href: 'tel:+441753720055' },
    { icon: MessageCircle, title: 'WhatsApp', value: '+44 07445137566', href: 'https://wa.me/447445137566' },
    { icon: Mail, title: 'Email', value: 'booking@aerotimecritical.co.uk', href: 'mailto:booking@aerotimecritical.co.uk' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Contact Aero Time Critical" description="Contact Aero Time Critical 24/7 for time-critical and AOG freight. Phone, email and WhatsApp support with rapid response." path="/contact" />
      <PageHero
        badge="GET IN TOUCH"
        BadgeIcon={Clock}
        title="Contact"
        titleAccent="Our Team"
        description="Available 24/7/365 to handle your urgent freight requirements. Reach out via phone, email or WhatsApp."
        image="https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/esv8b4gm_AOG%20ATC%20.png"
      />

      <section className="bg-[#F7F9FB] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 space-y-6"
            >
              <div>
                <h2 className="text-3xl font-bold mb-4 text-[#003057]">Get in Touch</h2>
                <p className="text-[#4A4A4A] text-lg leading-relaxed mb-8">
                  Our team is standing by to assist with quotes, bookings, and urgent shipments.
                </p>
              </div>

              {contactItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <a
                    key={index}
                    href={item.href}
                    target={item.title === 'WhatsApp' ? '_blank' : undefined}
                    rel={item.title === 'WhatsApp' ? 'noopener noreferrer' : undefined}
                    className="flex items-start gap-4 p-6 bg-white border border-[#E5E9EF] rounded-lg hover:border-[#0089D0] hover:shadow-md transition-all group"
                  >
                    <div className="w-11 h-11 bg-[#0089D0]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#0089D0] transition-colors">
                      <Icon className="text-[#0089D0] group-hover:text-white transition-colors" size={22} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1 text-[#003057]">{item.title}</h3>
                      <span className="text-[#4A4A4A] break-all">{item.value}</span>
                    </div>
                  </a>
                );
              })}

              <div className="flex items-start gap-4 p-6 bg-white border border-[#E5E9EF] rounded-lg">
                <div className="w-11 h-11 bg-[#0089D0]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#0089D0]" size={22} />
                </div>
                <div>
                  <h3 className="font-semibold mb-1 text-[#003057]">Address</h3>
                  <p className="text-[#4A4A4A]">268 Bath Road, Slough, Berkshire, SL1 4DX</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-6 bg-[#C9A227] rounded-lg">
                <Clock className="text-[#002240] flex-shrink-0" size={28} />
                <div>
                  <h3 className="font-bold text-[#002240]">24/7/365 Available</h3>
                  <p className="text-[#002240]/80 text-sm">Round-the-clock urgent support</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <div className="bg-white p-8 border border-[#E5E9EF] rounded-lg shadow-sm">
                <h3 className="text-2xl font-bold mb-8 text-[#003057]">Send us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-name" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Email *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="contact-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Phone</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleChange} data-testid="contact-phone" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Company</label>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} data-testid="contact-company" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Subject</label>
                      <input type="text" name="subject" value={formData.subject} onChange={handleChange} data-testid="contact-subject" placeholder="e.g., AOG enquiry, Air freight quote" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2">Preferred Contact Method</label>
                      <select name="preferredContact" value={formData.preferredContact} onChange={handleChange} data-testid="contact-preferred">
                        <option>Email</option>
                        <option>Telephone</option>
                        <option>WhatsApp</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={6} data-testid="contact-message" />
                  </div>

                  {/* Honeypot */}
                  <input type="text" name="website" value={formData.website} onChange={handleChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                  <label className="flex items-start gap-3 text-sm text-[#4A4A4A] cursor-pointer">
                    <input type="checkbox" name="consent" checked={formData.consent} onChange={handleChange} required className="w-auto mt-1" data-testid="contact-consent" />
                    <span>I consent to Aero Time Critical processing my data to respond to my enquiry, in line with the <a href="/privacy-policy" className="text-[#0089D0] underline">Privacy Policy</a>. *</span>
                  </label>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="btn-primary w-full flex items-center justify-center gap-2 text-lg"
                    data-testid="contact-submit"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <Send size={20} />
                  </motion.button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
